import { GradientText, SuccessIcon } from "lola-framework-ui-test";
import { useStore } from "../store/globalState";
import { useTranslation } from "react-i18next";
import {
  STATUS_TONE_COLOR,
  type StatusTone,
} from "../utils/operationStatus";
import {
  StatusCircleIcon,
  StatusFailedIcon,
  StatusPendingIcon,
} from "../assets/icons";
import { usablePersonName } from "../utils/usablePersonName";

export interface TransactionItemProps {
  statusKey: string;
  tone: StatusTone;
  amount: string;
  currency: string;
  date: string;
  payerName?: string;
  onSelect?: () => void;
}

/** Initials of first + last name, e.g. "Jane Doe" -> "JD". */
const getInitials = (name?: string): string => {
  const parts = (name || "").split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0][0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? "") : "";
  return `${first}${last}`.toUpperCase();
};

const statusIcon = (tone: StatusTone, color: string) => {
  switch (tone) {
    case "completed":
      return <SuccessIcon size={10} colors={[color, color]} />;
    case "failed":
    case "expired":
      return <StatusFailedIcon size={10} color={color} />;
    case "initial":
      return <StatusPendingIcon size={10} color={color} />;
    case "progress":
    default:
      return <StatusCircleIcon size={10} color={color} />;
  }
};

export const TransactionItem: React.FC<TransactionItemProps> = ({
  statusKey,
  tone,
  amount,
  currency,
  date,
  payerName,
  onSelect,
}) => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const colorConfig = theme.whiteView;
  const { bodyCopy } = colorConfig;

  const toneColor = STATUS_TONE_COLOR[tone];
  const label = t(statusKey);
  const selectable = typeof onSelect === "function";
  const displayName = usablePersonName(payerName);

  return (
    <section
      className="transactions-history-item"
      onClick={selectable ? onSelect : undefined}
      role={selectable ? "button" : undefined}
      tabIndex={selectable ? 0 : undefined}
      style={selectable ? { cursor: "pointer" } : undefined}
    >
      <p className="transactions-avatar">
        {displayName ? getInitials(displayName) : t("transactions.avatar")}
      </p>
      <section className="transactions-info">
        <div className="transactions-row transactions-row--primary">
          <GradientText className="transaction-title" textColor={bodyCopy}>
            <b>{displayName || t("transactions.transfers")}</b>
          </GradientText>
          <GradientText
            className="transactions-amount"
            textColor={bodyCopy}
            as="span"
          >
            ${amount} {currency}
          </GradientText>
        </div>
        <div className="transactions-row transactions-row--meta">
          <section className="transactions-status">
            {statusIcon(tone, toneColor)}
            <GradientText textColor={toneColor} as="span">
              {label}
            </GradientText>
          </section>
          <GradientText className="transactions-date" textColor={`${bodyCopy}5E`}>
            {date}
          </GradientText>
        </div>
      </section>
    </section>
  );
};
